import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../../components/forms/Buttons/Button";
import { downloadBookletDocument, isLiveJob, startBookletJob } from "../../services/bookletPrintService";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { cardClass, focusRing, Spinner } from "../Exams/TestBuilder/ui";
import DocumentList, { documentLabel, splitDocuments } from "./print/DocumentList";
import { useFocusKeeper } from "./print/focus";
import JobPanel, { jobAnnouncement, sentence } from "./print/JobPanel";
import { ConfirmDialog, DownloadLogDialog } from "./print/PrintDialogs";
import { BusyButton, Notice, phoneButtonClass, sectionHeaderClass, sectionTextClass, sectionTitleClass } from "./print/ui";
import useBookletDocuments from "./print/useBookletDocuments";
import useBookletJob from "./print/useBookletJob";

// What the section says about the PDFs it makes: previews with a watermark (a booklet not frozen now: never
// activated, or returned to draft, which drops the freeze), or the final PDFs of an active or archived one.
const introKey = (booklet) => {
  if (!booklet.frozen) return booklet.status === "draft" ? "bts.print.intro_draft" : "bts.print.intro_unfrozen";
  return booklet.status === "archived" ? "bts.print.intro_archived" : "bts.print.intro_active";
};

// Where the focus goes when the control that had it goes (a job starts, stops or ends): "Stop" while a job runs,
// "Try again" while its progress cannot be read, else the button that makes the PDFs (after a failure too: it is the
// way to try again, and the failed job's notice names it).
const FOCUS_TARGETS = ["cancel", "retry", "start"];

// What the section says of the job that kept a start from making the PDFs (`blocker`: {uuid, stopping}, from the
// 409), followed as the page's own one is (`job`, once read): under way from the booklet's earlier content, still
// stopping, or ended (the PDFs can be made now). Until it is read, the 409's word; nothing once the page shows another
// job under way.
const blockerMessage = (t, blocker, job) => {
  if (!blocker) return null;
  const blocking = job?.uuid === blocker.uuid ? job : null;
  if (!blocking && isLiveJob(job)) return null;
  if (blocking && !isLiveJob(blocking)) {
    return { ended: true, title: t("bts.print.blocked.ended_title"), text: t("bts.print.blocked.ended_text") };
  }
  return (blocking ? blocking.cancel_requested : blocker.stopping)
    ? { title: t("bts.print.blocked.stopping_title"), text: t("bts.print.blocked.stopping_text") }
    : { title: t("bts.print.blocked.running_title"), text: t("bts.print.blocked.running_text") };
};

function BookletPrintSection({ booklet, onBookletChange }) {
  const [t, i18n] = useTranslation();
  const uuid = booklet.uuid;
  const docs = useBookletDocuments(booklet);
  const reloadDocuments = docs.reload;
  // The job that ended while the page watched it: how it ended is said for it, until the booklet's status changes.
  const [endedJob, setEndedJob] = useState(null);
  // The job that kept the last start from making the PDFs (409 booklet_job_running): {uuid, stopping}, said until
  // the next start or the booklet's status changes. Nothing is started again by itself once it ends.
  const [blocker, setBlocker] = useState(null);
  const status = booklet.status;
  useEffect(() => {
    setEndedJob(null);
    setBlocker(null);
  }, [status]);
  const onEnded = useCallback(
    (job) => {
      setEndedJob(job.uuid);
      reloadDocuments();
      onBookletChange?.();
    },
    [reloadDocuments, onBookletChange]
  );
  const { job, stalled, pollError, follow, resume, cancel } = useBookletJob(booklet.documents?.job?.uuid, onEnded);
  const [starting, setStarting] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  // The documents being downloaded: each download adds and removes its own uuid, and a second click on one is
  // ignored (the ref at once, the state for the buttons).
  const downloadsRef = useRef(new Set());
  const [downloading, setDownloading] = useState(() => new Set());
  // The key asked about: kept while the dialog closes.
  const [keyConfirm, setKeyConfirm] = useState({ open: false, document: null });
  const [log, setLog] = useState({ open: false, document: null });
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const keepFocus = useFocusKeeper(sectionRef, () => [
    ...FOCUS_TARGETS.map((name) => sectionRef.current?.querySelector(`[data-print-focus="${name}"]`)),
    headingRef.current,
  ]);

  const sectionsCount = booklet.sections_count ?? booklet.check?.sections?.length ?? 0;
  const live = isLiveJob(job);
  const { current, retired, superseded } = splitDocuments(docs.documents, booklet.variant_numbers);
  const stale = current.some((document) => document.stale);
  // Previews of a booklet activated since: what is missing are its final PDFs.
  const finalNeeded = stale && !!booklet.frozen && current.every((document) => document.preview);
  // The latest job's current documents, and whether they are still what the booklet prints: "done" is said only
  // then (not once the booklet was activated, returned to draft or changed since).
  const jobDocuments = job ? current.filter((document) => document.job === job.uuid) : [];
  const fresh = jobDocuments.length > 0 && !jobDocuments.some((document) => document.stale);
  const watermark = booklet.labels?.preview || booklet.default_labels?.preview || "";

  const start = async () => {
    setStarting(true);
    setBlocker(null);
    try {
      const { job: started, created } = await startBookletJob(uuid);
      setEndedJob(null);
      follow(started, { wasLive: true });
      if (!created) toast.info(t("bts.print.already_running"));
    } catch (err) {
      // Another job is in the way (printing an earlier content of the booklet, or still stopping): followed, and
      // said above its progress.
      const blocking = getApiErrorCode(err) === "booklet_job_running" ? err.response?.data?.job : null;
      if (blocking?.uuid) {
        setBlocker({ uuid: blocking.uuid, stopping: !!blocking.cancel_requested });
        follow(blocking.uuid, { wasLive: true });
      } else {
        toast.error(getApiErrorMessage(t, err, "bts.print.start_error"));
      }
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    setCancelBusy(true);
    try {
      await cancel();
    } catch (err) {
      toast.error(getApiErrorMessage(t, err, "bts.print.cancel_error"));
    } finally {
      setCancelBusy(false);
    }
  };

  const runDownload = async (document) => {
    if (downloadsRef.current.has(document.uuid)) return;
    downloadsRef.current.add(document.uuid);
    setDownloading(new Set(downloadsRef.current));
    try {
      const { filename } = await downloadBookletDocument(document);
      toast.success(t("bts.print.downloaded", { name: filename }));
    } catch (err) {
      toast.error(getApiErrorMessage(t, err, "bts.print.download_error"));
    } finally {
      downloadsRef.current.delete(document.uuid);
      setDownloading(new Set(downloadsRef.current));
      // Its count and last download, or that it is gone.
      reloadDocuments();
    }
  };

  // The student booklet at once; the key only after the user agrees that it is logged.
  const download = (document) => {
    if (downloadsRef.current.has(document.uuid)) return;
    if (document.kind === "key") setKeyConfirm({ open: true, document });
    else runDownload(document);
  };

  const confirmKey = () => {
    setKeyConfirm((prev) => ({ ...prev, open: false }));
    if (keyConfirm.document) runDownload(keyConfirm.document);
  };

  const ended = !!job && job.uuid === endedJob;
  // The section's one button that makes the PDFs, named for what it makes now.
  const startLabel = finalNeeded ? t("bts.print.create_final") : current.length ? t("bts.print.recreate") : t("bts.print.create");
  const blocked = blockerMessage(t, blocker, job);
  // Its reads failed before it was read once: nothing below shows it, so its "Try again" is here.
  const blockedLost = !!blocked && job?.uuid !== blocker.uuid && !!pollError;
  const headingId = `booklet-print-${uuid}`;
  const keyDocument = keyConfirm.document;
  return (
    <section ref={sectionRef} aria-labelledby={headingId} className={cardClass} {...keepFocus}>
      <div className={sectionHeaderClass}>
        <div className="min-w-0">
          <h3 id={headingId} ref={headingRef} tabIndex={-1} className={`${sectionTitleClass} rounded ${focusRing}`}>
            <DocumentDuplicateIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            {t("bts.print.title")}
          </h3>
          <p className={sectionTextClass}>{t(introKey(booklet), { watermark })}</p>
          {!sectionsCount && <p className="mt-1 text-sm font-medium text-amber-700">{t("bts.print.no_sections_hint")}</p>}
        </div>
        {/* Blue while the final PDFs for print are missing or out of date (a draft's previews are optional: the
            page's step there is Activate). */}
        <BusyButton
          type="button"
          color={booklet.frozen && (!current.length || stale) ? "primary" : "secondary-outline"}
          Icon={PrinterIcon}
          text={startLabel}
          onClick={start}
          busy={starting}
          disabled={!sectionsCount || live}
          data-print-focus="start"
          className={`${phoneButtonClass} shrink-0`}
        />
      </div>

      {/* Always there, so that their changes are read out; the panels below are not live themselves. */}
      <p className="sr-only" aria-live="polite">
        {jobAnnouncement(t, job, { stalled, pollError, ended, fresh })}
      </p>
      <p className="sr-only" aria-live="polite">
        {blocked ? [blocked.title, blocked.text, blockedLost && t("bts.print.poll_error_text")].filter(Boolean).map(sentence).join(" ") : ""}
      </p>
      <div className="space-y-4 p-4 sm:p-5">
        {blocked && (
          <Notice
            tone={blocked.ended ? "info" : "warning"}
            Icon={blocked.ended ? InformationCircleIcon : ClockIcon}
            title={blocked.title}
            actions={
              blockedLost && (
                <Button
                  type="button"
                  color="secondary-outline"
                  Icon={ArrowPathIcon}
                  text={t("bts.print.retry")}
                  onClick={() => follow(blocker.uuid, { wasLive: true })}
                  data-print-focus="retry"
                  className={phoneButtonClass}
                />
              )
            }
          >
            <p className="mt-1">{blocked.text}</p>
            {blockedLost && <p className="mt-1">{t("bts.print.poll_error_text")}</p>}
          </Notice>
        )}

        <JobPanel
          booklet={booklet}
          job={job}
          stalled={stalled}
          pollError={pollError}
          ended={ended}
          current={jobDocuments.length > 0}
          fresh={fresh}
          cancelBusy={cancelBusy}
          onCancel={stop}
          onResume={resume}
          retryLabel={sectionsCount ? startLabel : ""}
        />

        {stale && !live && (
          <Notice tone="warning" Icon={ExclamationTriangleIcon} title={finalNeeded ? t("bts.print.final_needed_title") : t("bts.print.stale_title")}>
            <p className="mt-1">{finalNeeded ? t("bts.print.final_needed_text") : t("bts.print.stale_text")}</p>
          </Notice>
        )}

        {docs.documents === null ? (
          docs.error ? (
            <Notice
              tone="error"
              role="alert"
              title={t("bts.print.load_error")}
              actions={
                <Button type="button" color="secondary-outline" Icon={ArrowPathIcon} text={t("bts.print.retry")} onClick={reloadDocuments} className={phoneButtonClass} />
              }
            >
              <p className="mt-1">{getApiErrorMessage(t, docs.error, "bts.print.load_error")}</p>
            </Notice>
          ) : (
            <p className="flex items-center gap-2 py-4 text-sm text-slate-500" role="status">
              <Spinner />
              {t("bts.print.loading")}
            </p>
          )
        ) : (
          <DocumentList
            current={current}
            retired={retired}
            superseded={superseded}
            downloading={downloading}
            onDownload={download}
            onShowLog={(document) => setLog({ open: true, document })}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={keyConfirm.open}
        title={t("bts.print.key_confirm_title")}
        confirmText={t("buttons.download")}
        ConfirmIcon={ArrowDownTrayIcon}
        onConfirm={confirmKey}
        onCancel={() => setKeyConfirm((prev) => ({ ...prev, open: false }))}
      >
        <p className="font-medium text-slate-900">{t("bts.print.key_confirm_text")}</p>
        {keyDocument && <p>{documentLabel(t, keyDocument, i18n.language)}</p>}
        {(keyDocument?.retired || keyDocument?.superseded) && (
          <p className="font-medium text-amber-800">
            {keyDocument.retired ? t("bts.print.key_confirm_retired") : t("bts.print.key_confirm_replaced")}
          </p>
        )}
      </ConfirmDialog>
      <DownloadLogDialog
        isOpen={log.open}
        document={log.document}
        label={log.document ? documentLabel(t, log.document, i18n.language) : ""}
        onClose={() => setLog((prev) => ({ ...prev, open: false }))}
      />
    </section>
  );
}

/**
 * A booklet's PDFs on its page: the print job and its progress, the documents with their downloads and the
 * download log. `booklet` is the booklet's detail (GET exams/booklets/<uuid>/); `onBookletChange()` asks the
 * page to read the booklet again (a job ended: new documents). Another booklet starts afresh.
 */
export default function BookletPrint({ booklet, onBookletChange }) {
  if (!booklet?.uuid) return null;
  return <BookletPrintSection key={booklet.uuid} booklet={booklet} onBookletChange={onBookletChange} />;
}
